import { useEffect, useState } from 'react';
import { useFetchPodcastMetadata } from 'client/hooks/podcast/usePodcastMetadata';
import Promise from 'bluebird';
import {
  getFeatureFlag,
  NON_OPTIMIZELY_FEATURE_FLAGS,
} from 'helpers/featureFlags';
import { useCurrentUserCtx } from '../../../contexts/CurrentUser';
import { EPISODE_LIST_METADATA_STATE } from '../constants';
import { WORDPRESS_STATUS } from '../../../modules/AnchorAPI/v3/stations/fetchPodcastMetadata';
import { PAYWALLS_STATUS } from '../../../types/Metadata';
import AnchorAPI from '../../../modules/AnchorAPI';
import {
  SETUP_STRIPE_AND_PAYWALLS_URL,
  STRIPE_PAYWALLS_FLOW_INCOMPLETE,
} from '../../../components/PaywallsShared/constants';

export enum FETCH_WORDPRESS_STATUS {
  LOADING = 'LOADING',
  NOT_WORDPRESS = 'NOT_WORDPRESS',
}

export enum MONEY_STATUS {
  SKIP = 'SKIP',
  LOADING = 'LOADING',
  STRIPE_AUTHENTICATED = 'STRIPE_AUTHENTICATED',
  STRIPE_ELIGIBLE_AND_NO_AUTHENTICATED = 'STRIPE_ELIGIBLE_AND_NO_AUTHENTICATED ',
  ERROR = 'ERROR',
}

/**
 * on mount, this hook is responsible for fetching wordpress and paywalls status (depends on web station ID).
 */

export function useEpisodeListMetadata({
  isSubscribePath = false,
}: {
  isSubscribePath?: boolean;
}) {
  const [
    episodeListMetadataState,
    setEpisodeListMetadataState,
  ] = useState<EPISODE_LIST_METADATA_STATE>(EPISODE_LIST_METADATA_STATE.IDLE);
  const [wordpressStatus, setWordpressStatus] = useState<
    FETCH_WORDPRESS_STATUS | WORDPRESS_STATUS
  >(FETCH_WORDPRESS_STATUS.LOADING);
  const [paywallsStatus, setPaywallsStatus] = useState<PAYWALLS_STATUS>(
    PAYWALLS_STATUS.LOADING
  );
  const [moneyStatus, setMoneyStatus] = useState<MONEY_STATUS>(
    isSubscribePath ? MONEY_STATUS.LOADING : MONEY_STATUS.SKIP
  );

  const isStripeKycDataRequiredForPaidSubsSetupFeatureFlag =
    getFeatureFlag(
      NON_OPTIMIZELY_FEATURE_FLAGS.PAID_SUBS_SETUP_REQUIRES_STRIPE_KYC_DATA
    ) || false;

  const {
    state: {
      webStationId,
      status: currentUserState,
      error: currentUserError,
      userId,
    },
    fetchCurrentUser,
  } = useCurrentUserCtx();

  const {
    status: stationMetadataState,
    data: stationMetadata,
    refetch: handleFetchStationMetadata,
  } = useFetchPodcastMetadata();

  /**
   * set episode metadata state or set error state if it failed to fetch the station ID or station metadata
   */
  useEffect(() => {
    if (currentUserState === 'loading') {
      setEpisodeListMetadataState(
        EPISODE_LIST_METADATA_STATE.LOADING_STATION_ID
      );
    } else if (
      currentUserState === 'success' &&
      stationMetadataState === 'loading'
    ) {
      setEpisodeListMetadataState(
        EPISODE_LIST_METADATA_STATE.LOADING_STATION_METADATA
      );
    } else if (
      currentUserState === 'success' &&
      stationMetadataState === 'error'
    ) {
      setEpisodeListMetadataState(
        EPISODE_LIST_METADATA_STATE.LOADING_STATION_METADATA
      );
      handleFetchStationMetadata();
    } else if (
      stationMetadataState === 'success' &&
      moneyStatus !== MONEY_STATUS.LOADING
    ) {
      setEpisodeListMetadataState(EPISODE_LIST_METADATA_STATE.SUCCESS);
    } else if (currentUserError || stationMetadataState === 'error') {
      setEpisodeListMetadataState(EPISODE_LIST_METADATA_STATE.FAILED);
    }
  }, [
    currentUserState,
    currentUserError,
    stationMetadataState,
    moneyStatus,
    handleFetchStationMetadata,
  ]);

  /**
   * Fetch Money Status if the current location is subscribe path (/dashboard/episodes/subscribe)
   * Check if the user is allowed to use Stripe and if it's already authenticated or not.
   * This will determine if the user needs to activate Stripe before proceeding to Paywalls setup.
   */
  useEffect(() => {
    const handleSubscribePath = async () => {
      if (isSubscribePath && webStationId && userId) {
        const [
          moneyStatusResponse,
          currentPodcastWalletResponse,
        ] = await Promise.all([
          AnchorAPI.fetchMoneyStatus({
            webStationId,
            userId,
          }),
          AnchorAPI.fetchCurrentPodcastWallet({
            shouldBustCache: true,
          }),
        ]);

        const status = moneyStatusResponse.isAllowedToAuthenticateStripe
          ? moneyStatusResponse.hasAuthenticatedStripe
            ? MONEY_STATUS.STRIPE_AUTHENTICATED
            : MONEY_STATUS.STRIPE_ELIGIBLE_AND_NO_AUTHENTICATED
          : MONEY_STATUS.ERROR;

        // Trigger Stripe setup right away if user is not authenticated, no need to wait for the rest of data.
        // For feature flag: Also trigger Stripe setup if the user somehow got to the subscribe path without having uploaded all KYC data.
        if (status === MONEY_STATUS.STRIPE_ELIGIBLE_AND_NO_AUTHENTICATED) {
          window.location.replace(SETUP_STRIPE_AND_PAYWALLS_URL);
        } else if (
          isStripeKycDataRequiredForPaidSubsSetupFeatureFlag &&
          status === MONEY_STATUS.STRIPE_AUTHENTICATED &&
          !currentPodcastWalletResponse.stripeTransfersEnabled
        ) {
          window.location.replace(
            `/dashboard/money?from=${STRIPE_PAYWALLS_FLOW_INCOMPLETE}`
          );
        } else {
          setMoneyStatus(status);
        }
      }
    };

    handleSubscribePath().catch(error => console.log(error));
  }, [isSubscribePath, webStationId, userId]);

  useEffect(() => {
    if (stationMetadata) {
      /**
       * return three main states of the given user's status as it pertains to wordpress:
       *
       * 1. FETCH_WORDPRESS_STATUS.LOADING: we are fetching the user's station metadata
       *
       * we have successfully fetched user's station metadata and...
       * 2. FETCH_WORDPRESS_STATUS.NOT_WORDPRESS:
       *    user was not referred to Anchor from WordPress
       * 3. stationMetadata.wordpressStatus (WORDPRESS_STATUS enum):
       *    user was referred to Anchor from WordPress
       */
      setWordpressStatus(
        stationMetadata.wordpressStatus || FETCH_WORDPRESS_STATUS.NOT_WORDPRESS
      );
      /**
       * Calculate and set paywalls status.
       * Paywalls can be enabled for the podcast or not, and if it's enabled can be setup or not.
       * As we roll this out to more users, we should also check the feature flag
       */
      setPaywallsStatus(
        stationMetadata.isPWSetup
          ? PAYWALLS_STATUS.ENABLED_AND_SETUP
          : PAYWALLS_STATUS.ENABLED_AND_NOT_SETUP
      );
    }
  }, [stationMetadata]);

  const handleFetchMetadata = () => {
    if (!webStationId) {
      fetchCurrentUser();
    } else {
      handleFetchStationMetadata();
    }
  };

  return {
    userId,
    webStationId,
    episodeListMetadataState,
    wordpressStatus,
    paywallsStatus,
    moneyStatus,
    handleFetchMetadata,
  };
}
